import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Plus, Banknote, AlertCircle, Loader2, RefreshCw, TrendingDown, TrendingUp, Wallet, FileText, Sparkles, Upload, Pencil, CheckCircle2, AlertTriangle, Paperclip, UserCheck } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { formatPYG, formatDate } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { roleAllowed } from "../utils/roles";
import SelectBuscable from "../components/SelectBuscable";   // Fix ED (2026-09-24)
import { pdfCierreCajaChica } from "../utils/cierreCajaChicaPdf";   // Fix EZ (2026-10-05)
import { PM_CATEGORIAS } from "./compras/PedidosMaterialesTab";   // Fix FH (2026-10-08)

// Fix FH (2026-10-08): "al momento de vincular el gasto de caja chica a un presupuesto, debe
// salir las categorías como lo hacemos en Compras de materiales" (CEN). Como en el pedido,
// solo "Material (del Pto.)" exige el ítem del presupuesto; las demás quedan imputadas con
// su categoría (el ítem es opcional).
const categoriaLabel = (v) => (PM_CATEGORIAS.find(c => c.value === v) || {}).label || v;
const lineaVinculada = (l) => !!(l && l.item_uid) || ((l && l.categoria) || "listado") !== "listado";

// ─── Modal simple ────────────────────────────────────────────
function Modal({ open, onClose, title, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Fix ED (2026-09-24): "queda estatica la ventana de carga y si se da clic fuera de
          ella sale toda la carga se pierde, ademas no permite visualizar la confirmacion
          final". La ventana entra en la pantalla y se desplaza; clic afuera ya no la
          cierra (se cierra con ✕ o Cancelar). */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col" data-testid="caja-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <h3 className="font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100" data-testid="caja-modal-cerrar">✕</button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}

const CATEGORIES = ["Gastos menores", "Transporte", "Alimentación", "Papelería", "Otros"];
// Fix FZ (2026-10-14): el gasto indirecto se categoriza con las categorías de Costos indirectos (OPE).
const CATEGORIES_INDIRECTO = ["Servicios Públicos", "Transporte", "Insumos de Oficina", "Equipos", "Mantenimiento", "Comunicaciones", "Software/Licencias", "Otros"];

/* Fix CT (2026-09-14): "cuando una línea de factura no se encuentre vinculada a un Ítem
   del Presupuesto tendrá una leyenda de 'Pendiente de vinculación', donde el responsable
   de la obra podrá hacer una carga retroactiva de los datos de Pto, ítem del Pto, Tarea,
   para poder imputar correctamente el gasto. Una vez cerrada la regularización de la
   imputación el estado de la factura pasará a 'Vinculación cerrada'" (Sergio, 14/09).
   Solo escribe la imputación: no toca descripciones, cantidades, precios ni el monto. */
function VincularModal({ gasto, onClose, onSaved }) {
  // Fix EF (2026-09-24): un gasto sin desglose se vincula como una sola línea por el total.
  const [lineas, setLineas] = useState(() => ((gasto.items || []).length ? gasto.items
    : [{ descripcion: gasto.description || "", cantidad: 1, unidad: "", precio_unit: gasto.total_with_iva || 0, total: gasto.total_with_iva || 0 }])
    .map(l => ({ ...l })));
  const [cat, setCat] = useState({ items_pto: [], niveles: [], tareas: [], tareas_por_item: {} });
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [autocompletadas, setAutocompletadas] = useState(0);   // Fix HM: líneas a las que se les puso el nivel / la tarea del presupuesto
  const lineasRef = useRef(lineas);
  lineasRef.current = lineas;
  // Fix EF (2026-09-24): la obra se elige acá; en la carga queda solo la referencia.
  const [obraId, setObraId] = useState(gasto.obra_id || "");
  const [obras, setObras] = useState([]);
  const opts = { withCredentials: true };

  useEffect(() => {
    axios.get(`${API}/obras`, opts).then(({ data }) => setObras(Array.isArray(data) ? data : (data?.obras || []))).catch(() => setObras([]));
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!obraId) { setCat({ items_pto: [], niveles: [], tareas: [], tareas_por_item: {} }); setCargando(false); return; }
    setCargando(true);
    axios.get(`${API}/obras/${obraId}/catalogos`, opts)
      .then(({ data }) => {
        const c = { items_pto: data.items_pto || [], niveles: data.niveles || [], tareas: data.tareas || [], tareas_por_item: data.tareas_por_item || {} };
        setCat(c);
        // Fix HM (2026-10-22): "si viene desde compras ya tiene que venir por defecto ya vinculada en su totalidad, tantos
        // los niveles como tareas" (CEN). A la línea que ya tiene ítem del presupuesto se le completa lo que le falta: el
        // nivel del ítem y, si el ítem tiene una sola tarea, esa tarea. Lo que ya traía no se toca.
        {
          let n = 0;
          const out = lineasRef.current.map(l => {
            const it = l.item_uid ? c.items_pto.find(x => x.item_uid === l.item_uid) : null;
            if (!it) return l;
            const patch = {};
            if (!l.nivel_id && it.nivel_id) {
              patch.nivel_id = it.nivel_id;
              patch.nivel_label = (c.niveles.find(nv => nv.id === it.nivel_id) || {}).label || "";
            }
            const ts = c.tareas_por_item[l.item_uid] || [];
            if (!l.tarea_numero && ts.length === 1) {
              patch.tarea_numero = String(ts[0]);
              patch.tarea_nombre = (c.tareas.find(t => String(t.numero) === String(ts[0])) || {}).nombre || "";
            }
            if (!Object.keys(patch).length) return l;
            n += 1;
            return { ...l, ...patch };
          });
          setAutocompletadas(n);
          if (n) setLineas(out);
        }
      })
      .catch(() => toast.error("No se pudieron traer los ítems del presupuesto de la obra"))
      .finally(() => setCargando(false));
  }, [obraId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const elegirObra = (v) => {
    if (v === obraId) return;
    setObraId(v);
    // Con otra obra, lo imputado a la anterior ya no vale.
    setLineas(prev => prev.map(l => ({ ...l, presupuesto_id: "", item_uid: "", item_label: "", nivel_id: "", nivel_label: "", tarea_numero: "", tarea_nombre: "" })));
  };

  const setL = (i, patch) => setLineas(prev => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const elegirItem = (i, uid) => {
    const it = cat.items_pto.find(x => x.item_uid === uid);
    const niv = cat.niveles.find(n => n.id === (it?.nivel_id || ""));
    // Fix HM: si el ítem tiene una sola tarea en el presupuesto, la línea la toma (si no, queda la que tenía).
    const ts = (cat.tareas_por_item || {})[uid] || [];
    const tarea = ts.length === 1 ? cat.tareas.find(t => String(t.numero) === String(ts[0])) : null;
    setL(i, { item_uid: uid, item_label: it ? `${it.numero} ${it.descripcion}`.trim() : "",
              nivel_id: it?.nivel_id || "", nivel_label: niv?.label || "",
              ...(tarea ? { tarea_numero: String(tarea.numero), tarea_nombre: tarea.nombre || "" } : {}) });
  };
  const elegirTarea = (i, num) => {
    const t = cat.tareas.find(x => String(x.numero) === String(num));
    setL(i, { tarea_numero: String(num || ""), tarea_nombre: t?.nombre || "" });
  };

  const sinVincular = lineas.filter(l => !lineaVinculada(l)).length;   // Fix FH

  const guardar = async (cerrar) => {
    setGuardando(true);
    try {
      await axios.put(`${API}/purchases/petty-cash/${gasto._id}/vincular`, { items: lineas, cerrar, obra_id: obraId }, opts);
      toast.success(cerrar ? "Vinculación cerrada" : "Imputación guardada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar la vinculación"); }
    finally { setGuardando(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="vincular-modal">
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="font-bold text-gray-900">Vincular al presupuesto</p>
            <p className="text-xs text-gray-400">{gasto.numero} · {gasto.description} · {gasto.obra_nombre || (gasto.obra_referencia ? `Ref.: ${gasto.obra_referencia}` : "Sin obra")}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="vincular-cerrar">✕</button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Obra</label>
            <SelectBuscable value={obraId} onChange={elegirObra}
              options={obras.map(o => ({ value: o._id, label: `${o.codigo} · ${o.nombre}` }))}
              vacio="Elegí la obra…" placeholderBuscar="Buscar obra..."
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" testId="vincular-obra" />
            {gasto.obra_referencia && <p className="text-[11px] text-gray-500 mt-1" data-testid="vincular-referencia">Referencia cargada: <strong>{gasto.obra_referencia}</strong></p>}
          </div>
          {!obraId ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="vincular-sin-obra">
              Elegí la obra para ver los ítems de su presupuesto.
            </p>
          ) : cargando ? (
            <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : (
            <>
              {autocompletadas > 0 && (
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="vincular-autocompletado">
                  {autocompletadas === 1 ? "Una línea tenía" : `${autocompletadas} líneas tenían`} el nivel o la tarea sin completar: se completaron con lo que define el presupuesto. Revisalo y guardá.
                </p>
              )}
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase">Líneas de la factura</p>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${sinVincular ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
                  data-testid="vincular-contador">
                  {sinVincular ? `${sinVincular} pendiente(s) de vinculación` : "Todas las líneas vinculadas"}
                </span>
              </div>
              <div className="space-y-2">
                {lineas.map((l, i) => (
                  <div key={i} className={`rounded-xl border p-3 ${lineaVinculada(l) ? "border-gray-100" : "border-amber-200 bg-amber-50/40"}`} data-testid={`vincular-linea-${i}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {l.cantidad ? `${l.cantidad}${l.unidad ? " " + l.unidad : ""} × ` : ""}{l.descripcion}
                      </p>
                      <span className="text-sm font-semibold tabular-nums flex-shrink-0">{formatPYG(l.total || 0)}</span>
                    </div>
                    {!lineaVinculada(l) && <p className="text-[11px] text-amber-700 font-semibold mb-1.5" data-testid={`vincular-leyenda-${i}`}>Pendiente imputación a Pto</p>}
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                      {/* Fix FH (2026-10-08): categoría de la línea, como en Compras de materiales. */}
                      <select value={l.categoria || "listado"} onChange={e => setL(i, { categoria: e.target.value })}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid={`vincular-categoria-${i}`}>
                        {PM_CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      {/* Fix ED (2026-09-24): con buscador. */}
                      <SelectBuscable value={l.item_uid || ""} onChange={v => elegirItem(i, v)}
                        options={cat.items_pto.map(it => ({ value: it.item_uid, label: `${it.numero} — ${it.descripcion}` }))}
                        vacio={(l.categoria || "listado") === "listado" ? "Ítem del Pto…" : "Ítem del Pto (opcional)…"} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" testId={`vincular-item-${i}`} />
                      <SelectBuscable value={l.nivel_id || ""} onChange={v => {
                          const n = cat.niveles.find(x => x.id === v);
                          setL(i, { nivel_id: v, nivel_label: n?.label || "" });
                        }}
                        options={cat.niveles.map(n => ({ value: n.id, label: n.label }))}
                        vacio="Nivel…" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" testId={`vincular-nivel-${i}`} />
                      <SelectBuscable value={l.tarea_numero || ""} onChange={v => elegirTarea(i, v)}
                        options={cat.tareas.map(t => ({ value: t.numero, label: `${t.numero} — ${t.nombre}` }))}
                        vacio="Tarea…" className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" testId={`vincular-tarea-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
                <button onClick={() => guardar(false)} disabled={guardando}
                  className="flex-1 border border-[#2E6DA4] text-[#2E6DA4] rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="vincular-guardar">
                  Guardar avance
                </button>
                <button onClick={() => guardar(true)} disabled={guardando || sinVincular > 0}
                  title={sinVincular ? "Primero vinculá todas las líneas" : ""}
                  className="flex-1 bg-[#2E6DA4] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-40" data-testid="vincular-cerrar-btn">
                  Cerrar vinculación
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>, document.body
  );
}

const ALLOWED_ROLES = [
  "Superadministrador", "Administrador", "Compras",
  "Coordinador de Obra", "Ingeniero Residente", "Gerente Tecnico",
  "Gerencia Financiera",   // Fix DQ (2026-09-22)
];
// Fix DQ (2026-09-22): quién registra ingresos y cierra la caja.
const CAJA_ROLES = ["Superadministrador", "Administrador", "Gerencia Financiera",
  "Gerente Tecnico", "Gerencia Técnica", "Jefe de Compras", "Contador"];
// Fix EZ (2026-10-05): IVA 5 %, comprobante y fondo fijo (CEN, OBS SISTEMA).
const TASAS_IVA = { "10": 0.1, "5": 0.05, exenta: 0 };
const FONDO_ROLES = ["Superadministrador", "Administrador", "Gerencia Financiera", "Contador", "Director"];
// Los gastos de antes no tienen el campo: sin números en el N° («SIN FACTURA») son sin factura.
const sinFactura = (g) => g.comprobante_tipo === "sin_factura" || (!g.comprobante_tipo && !/\d/.test(g.invoice_number || g.numero_comprobante || ""));
// Fix GC (2026-10-14): las compras que nacen en Análisis Ppto («Compra desde caja chica») las aprueban
// (APROB / RECH / PAGAR) Gerencia Financiera, Gerencia Técnica o Dirección. Una vez pagadas se carga el
// comprobante, que se lee siempre con IA. Solicitada, aprobada y rechazada no cuentan como gasto de la caja.
// Fix HD (2026-10-20): quién aprueba se configura (botón «Quién aprueba»); lo informa el servidor (`puede_aprobar`).
const COMPRA_COMPROBANTE_ROLES = ["Superadministrador", "Administrador", "Compras", "Coordinador de Obra", "Ingeniero Residente",
  "Gerente Tecnico", "Contador", "Gerencia Financiera"];
const COMPRA_LABEL = { solicitada: "Solicitada · a aprobar", aprobada: "Aprobada · a pagar", pagada: "Pagada", rechazada: "Rechazada" };
const COMPRA_BADGE = { solicitada: "bg-amber-100 text-amber-700", aprobada: "bg-blue-100 text-blue-700",
  pagada: "bg-emerald-100 text-emerald-700", rechazada: "bg-red-100 text-red-700" };
const compraSinResolver = (g) => ["solicitada", "aprobada", "rechazada"].includes(g.compra_estado);
const MEDIOS_INGRESO = [
  { value: "efectivo", label: "Efectivo" }, { value: "transferencia", label: "Transferencia" },
  { value: "cheque", label: "Cheque" }, { value: "otro", label: "Otro" },
];

export default function ComprasCajaChica() {
  const { user } = useAuth();
  const [pettyItems, setPettyItems] = useState([]);   // Fix CQ (2026-09-14): `items` ahora es el desglose de la factura
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState("");
  // Fix CT (2026-09-14): filtros para que el que pidió ubique su gasto, y la ventana
  // de vinculación retroactiva contra el presupuesto.
  const [filterObra, setFilterObra] = useState("");
  const [filterSolicitante, setFilterSolicitante] = useState("");
  const [filterVinc, setFilterVinc] = useState("");
  const [vincular, setVincular] = useState(null);
  const [showNew, setShowNew] = useState(false);
  // Fix CQ (2026-09-14): solicitante, obra a imputar y desglose de la factura.
  const VACIO = { tipo_gasto: "directo", project_id: "", obra_id: "", obra_referencia: "", solicitante: "", autorizado_por: "", amount: "", description: "", category: "Gastos menores", provider: "", date: "", invoice_number: "", iva_tipo: "10", comprobante_tipo: "factura" };   // Fix EN: iva_tipo · Fix EZ: comprobante_tipo
  const [form, setForm] = useState(VACIO);
  const [obras, setObras] = useState([]);
  const [items, setItems] = useState([]);
  const [validada, setValidada] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiInfo, setAiInfo] = useState(null);  // { confidence, requires_manual_review, provider, ruc }
  const fileInputRef = useRef(null);
  // Fix HJ (2026-10-21): "en caja chica debe quedar subida la factura también" (CEN): el archivo de la factura queda guardado con el gasto.
  const [factura, setFactura] = useState(null);   // { name, type, data }: el archivo elegido (se lee con IA y se guarda con el gasto)
  const soloFacturaRef = useRef(null);
  const facturaRef = useRef(null);
  const [facturaDe, setFacturaDe] = useState(null);   // gasto ya cargado al que se le adjunta la factura
  // Fix DQ (2026-09-22): ingresos de reposición, saldo en tiempo real y cierres.
  const [saldo, setSaldo] = useState(null);
  const [cierres, setCierres] = useState([]);
  const INGRESO_VACIO = { monto: "", fecha: "", medio: "efectivo", origen: "", numero_comprobante: "", autorizado_por: "" };
  const [ingreso, setIngreso] = useState(null);      // null = modal cerrado
  const [cierreModal, setCierreModal] = useState(false);
  const [cierreObs, setCierreObs] = useState("");
  const [guardandoCaja, setGuardandoCaja] = useState(false);
  const [editando, setEditando] = useState(null);   // Fix EZ (2026-10-05): gasto que se edita
  const [fondoInput, setFondoInput] = useState("");
  const [borrar, setBorrar] = useState(null);   // id del gasto a confirmar para eliminar
  // Fix GC (2026-10-14): circuito de las compras desde Análisis Ppto.
  const [filterCompra, setFilterCompra] = useState("");
  const [rechazando, setRechazando] = useState(null);   // { gasto, motivo }
  const [pagando, setPagando] = useState(null);   // id de la solicitud a confirmar para pagar
  const [trabajando, setTrabajando] = useState(null);   // id de la solicitud en proceso
  const [comprobanteDe, setComprobanteDe] = useState(null);
  const comprobanteRef = useRef(null);
  // Fix HD (2026-10-20): "que se pueda configurar quiénes pueden aprobar" los pedidos de pago por caja chica que vienen de compras.
  const [aprob, setAprob] = useState(null);   // { roles, user_ids, aprueban, puede_aprobar, puede_editar, roles_disponibles, usuarios }
  const [aprobModal, setAprobModal] = useState(null);   // borrador { roles, user_ids } · null = cerrado
  const [guardandoAprob, setGuardandoAprob] = useState(false);

  const canCreate = roleAllowed(user?.role, ALLOWED_ROLES);
  const canCaja = roleAllowed(user?.role, CAJA_ROLES);
  const canFondo = roleAllowed(user?.role, FONDO_ROLES);
  const canAprobarCompra = !!(aprob && aprob.puede_aprobar);   // Fix GC · Fix HD: lo configura «Quién aprueba»
  const canComprobante = roleAllowed(user?.role, COMPRA_COMPROBANTE_ROLES);   // Fix GC
  const opts = { withCredentials: true };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pcRes, projRes, obrasRes, saldoRes, cierresRes, aprobRes] = await Promise.all([
        axios.get(`${API}/purchases/petty-cash`, opts),
        axios.get(`${API}/projects`, opts),
        axios.get(`${API}/obras`, opts).catch(() => ({ data: [] })),   // Fix CQ (2026-09-14)
        axios.get(`${API}/caja-chica/saldo`, opts).catch(() => ({ data: null })),       // Fix DQ (2026-09-22)
        axios.get(`${API}/caja-chica/cierres?limit=20`, opts).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/aprobadores-caja-chica`, opts).catch(() => ({ data: null })),   // Fix HD
      ]);
      setPettyItems(pcRes.data);
      setProjects(projRes.data);
      setObras((obrasRes.data || []).filter(o => !o.deleted_at));
      setSaldo(saldoRes.data || null);
      setCierres(Array.isArray(cierresRes.data) ? cierresRes.data : []);
      setAprob(aprobRes.data || null);   // Fix HD
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const cerrarModal = () => { setShowNew(false); setAiInfo(null); setItems([]); setValidada(false); setForm(VACIO); setEditando(null); setFactura(null); };   // Fix HJ: factura

  // Fix EZ (2026-10-05): "Permitir editar los gastos cargados hasta que se cierre".
  const abrirEdicion = (g) => {
    setForm({ ...VACIO, tipo_gasto: g.tipo_gasto || "directo", obra_referencia: g.obra_referencia || "", solicitante: g.solicitante || "", autorizado_por: g.autorizado_por || "",
      amount: String(g.amount ?? ""), description: g.description || "", category: g.category || "Gastos menores", provider: g.provider || "",
      date: g.date || "", invoice_number: /\d/.test(g.invoice_number || "") ? g.invoice_number : "",
      iva_tipo: sinFactura(g) ? "exenta" : (TASAS_IVA[g.iva_tipo] !== undefined ? g.iva_tipo : "10"),
      comprobante_tipo: sinFactura(g) ? "sin_factura" : "factura" });
    setItems((g.items || []).map(l => ({ ...l })));
    setValidada(!!g.lectura_validada_por);
    setAiInfo(null);
    setFactura(null);   // Fix HJ: el archivo ya guardado queda; solo se envía uno nuevo si se elige
    setEditando(g);
    setShowNew(true);
  };

  const eliminarGasto = async (g) => {
    try {
      await axios.delete(`${API}/purchases/petty-cash/${g._id}`, opts);
      toast.success(`Gasto ${g.numero || ""} eliminado`);
      setBorrar(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar el gasto"); }
  };

  // Fix GC (2026-10-14): aprobar / rechazar / pagar la solicitud y cargar su comprobante.
  const accionCompra = async (g, accion, body, okMsg) => {
    setTrabajando(g._id);
    try {
      await axios.put(`${API}/purchases/petty-cash/${g._id}/compra/${accion}`, body || {}, opts);
      toast.success(okMsg);
      setPagando(null); setRechazando(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo completar la acción"); }
    finally { setTrabajando(null); }
  };

  // Fix HD (2026-10-20): guardar quién aprueba (aprobar / rechazar / pagar) las compras de Análisis Ppto.
  const guardarAprob = async () => {
    setGuardandoAprob(true);
    try {
      const r = await axios.put(`${API}/settings/aprobadores-caja-chica`, { roles: aprobModal.roles, user_ids: aprobModal.user_ids }, opts);
      setAprob(r.data);
      setAprobModal(null);
      toast.success("Quién aprueba en Caja Chica quedó guardado");
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
    finally { setGuardandoAprob(false); }
  };

  const subirComprobante = async (e) => {
    const file = e.target.files && e.target.files[0];
    const g = comprobanteDe;
    e.target.value = "";
    if (!file || !g) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo supera los 5MB"); return; }
    setTrabajando(g._id);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      const { data } = await axios.post(`${API}/purchases/petty-cash/${g._id}/comprobante`,
        { file_data: dataUrl, file_name: file.name, file_type: file.type || "application/pdf" }, opts);
      if (data.verificacion?.estado === "verificado") toast.success("Comprobante cargado y verificado por IA");
      else toast.warning(`Comprobante cargado con diferencias: ${(data.verificacion?.diferencias || []).join(" · ")}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo cargar el comprobante"); }
    finally { setTrabajando(null); setComprobanteDe(null); }
  };

  // Fix HJ (2026-10-21): adjuntar la factura a un gasto ya cargado (sin IA, no toca montos ni imputación).
  const subirFactura = async (e) => {
    const file = e.target.files && e.target.files[0];
    const g = facturaDe;
    e.target.value = "";
    if (!file || !g) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo supera los 5MB"); return; }
    setTrabajando(g._id);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
      await axios.post(`${API}/purchases/petty-cash/${g._id}/factura`,
        { file_data: dataUrl, file_name: file.name, file_type: file.type || "application/pdf" }, opts);
      toast.success(`Factura subida al gasto ${g.numero || ""}`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo subir la factura"); }
    finally { setTrabajando(null); setFacturaDe(null); }
  };

  // Fix HJ: elegir solo el archivo de la factura (sin leerla con IA) en el formulario del gasto.
  const elegirSoloFactura = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo supera los 5MB"); return; }
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"].includes(file.type)) { toast.error("Sólo PDF o imagen (PNG/JPG/WEBP)"); return; }
    const data = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file); });
    setFactura({ name: file.name, type: file.type, data });
  };

  const guardarFondo = async () => {
    setGuardandoCaja(true);
    try {
      const { data } = await axios.put(`${API}/caja-chica/fondo`, { monto: Number(fondoInput) || 0 }, opts);
      setSaldo(data);
      toast.success("Fondo fijo guardado");
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar el fondo"); }
    finally { setGuardandoCaja(false); }
  };

  const descargarPdfCierre = async (c) => {
    try {
      const { data } = await axios.get(`${API}/caja-chica/cierres/${c._id}`, opts);
      pdfCierreCajaChica(data);
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo generar el PDF del cierre"); }
  };

  // Fix DQ (2026-09-22): el ingreso de reposición y el cierre de la caja.
  const guardarIngreso = async (e) => {
    e.preventDefault();
    if (!(Number(ingreso.monto) > 0)) { toast.error("Ingresá el monto de la reposición"); return; }
    setGuardandoCaja(true);
    try {
      await axios.post(`${API}/caja-chica/ingresos`, { ...ingreso, monto: Number(ingreso.monto) }, opts);
      toast.success("Ingreso registrado");
      setIngreso(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo registrar el ingreso"); }
    finally { setGuardandoCaja(false); }
  };

  const cerrarCaja = async () => {
    setGuardandoCaja(true);
    try {
      const { data } = await axios.post(`${API}/caja-chica/cierres`, { observaciones: cierreObs }, opts);
      toast.success(`Cierre ${data?.cierre?.numero || ""}: solicitud de reposición por ${formatPYG(data?.cierre?.monto_reposicion || 0)}`);
      setCierreModal(false); setCierreObs("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo cerrar la caja"); }
    finally { setGuardandoCaja(false); }
  };

  const reponerCierre = async (c) => {
    setGuardandoCaja(true);
    try {
      await axios.post(`${API}/caja-chica/cierres/${c._id}/reponer`, { medio: "transferencia" }, opts);
      toast.success("Reposición registrada: el saldo ya la toma");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo registrar la reposición"); }
    finally { setGuardandoCaja(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.description) { toast.error("Complete todos los campos obligatorios"); return; }
    if (!form.solicitante.trim()) { toast.error("Poné el nombre del solicitante"); return; }
    // Fix EI (2026-09-27): la carga lleva solo la obra de referencia; el proyecto sale de la
    // obra que se elige en "Vincular al presupuesto".
    // Fix CQ (2026-09-14): "con la validación de la lectura por el que carga la
    // misma" (Sergio, 14/09). Si hay desglose, no se guarda sin tildar que se revisó.
    if (items.length > 0 && !validada) { toast.error("Revisá el desglose de la factura y tildá la validación"); return; }
    if (form.comprobante_tipo === "factura" && !(form.provider.trim() && /\d/.test(form.invoice_number))) {   // Fix EZ
      toast.error("Con factura, cargá el proveedor y el N° de factura"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form, amount: Number(form.amount),
        items: items.map(i => ({ ...i, cantidad: Number(i.cantidad) || 0, precio_unit: Number(i.precio_unit) || 0, total: Number(i.total) || 0 })),
        lectura_validada: validada,
        ai_confidence: aiInfo?.confidence ?? null,
        ...(factura ? { factura_data: factura.data, factura_name: factura.name, factura_type: factura.type } : {}),   // Fix HJ
      };
      if (editando) await axios.put(`${API}/purchases/petty-cash/${editando._id}`, payload, opts);   // Fix EZ
      else await axios.post(`${API}/purchases/petty-cash`, payload, opts);
      toast.success(editando ? "Gasto actualizado" : "Gasto de caja chica registrado");
      cerrarModal();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleAIUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // reset
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El archivo supera los 5MB");
      return;
    }
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      toast.error("Sólo PDF o imagen (PNG/JPG/WEBP)");
      return;
    }
    setAiLoading(true);
    setAiInfo(null);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setFactura({ name: file.name, type: file.type, data: base64 });   // Fix HJ: la factura que se lee queda guardada con el gasto
      const res = await axios.post(`${API}/purchases/petty-cash/ai-extract`, {
        file_data: base64, file_name: file.name, file_type: file.type,
      }, opts);
      const d = res.data;
      setForm(prev => {
        // Fix EZ (2026-10-05): con N° de factura es factura; IVA 5 % si el IVA leído ronda el 5 % del neto.
        const comprobante_tipo = d.invoice_number ? "factura" : prev.comprobante_tipo;
        // Fix EN (2026-09-29): si la factura no trae IVA, el gasto queda sin IVA.
        const ivaLeido = d.amount_pyg ? (Number(d.iva_amount) > 0 ? (Number(d.iva_amount) / Number(d.amount_pyg) < 0.075 ? "5" : "10") : "exenta") : prev.iva_tipo;
        return {
          ...prev,
          amount: d.amount_pyg ? String(d.amount_pyg) : prev.amount,
          description: d.description || prev.description,
          date: d.date || prev.date,
          provider: d.provider_name || prev.provider,
          invoice_number: d.invoice_number || prev.invoice_number,
          comprobante_tipo,
          iva_tipo: comprobante_tipo === "sin_factura" ? "exenta" : ivaLeido,
        };
      });
      setAiInfo({
        confidence: d.confidence || 0,
        requires_manual_review: d.requires_manual_review,
        ruc: d.provider_ruc,
        iva_amount: d.iva_amount,
        total_with_iva: d.total_with_iva,
      });
      // Fix CQ (2026-09-14): el desglose por ítem entra a la tabla editable y la
      // validación vuelve a cero — hay que revisarla de nuevo en cada lectura.
      setItems(d.items || []);
      setValidada(false);
      if (d.requires_manual_review) {
        toast.warning(`IA con confianza ${d.confidence}%. Revisá los valores.`);
      } else {
        toast.success(`Datos extraídos por IA (confianza ${d.confidence}%)${(d.items || []).length ? ` · ${d.items.length} ítem(s)` : ""}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al analizar la factura con IA");
    } finally {
      setAiLoading(false);
    }
  };

  // Fix CT (2026-09-14): solicitante / obra / local / estado de vinculación.
  const textoF = filterSolicitante.trim().toLowerCase();
  const filtered = pettyItems.filter(i =>
    (!filterProject || i.project_id === filterProject)
    && (!filterObra || (filterObra === "__sin_obra__" ? !i.obra_id : i.obra_id === filterObra))
    && (!filterCompra || (filterCompra === "pendiente_comprobante" ? i.pendiente_comprobante : i.compra_estado === filterCompra))
    && (!filterVinc || (filterVinc === "pendiente" ? ["pendiente", "sin_desglose"].includes(i.vinculacion_estado || "sin_desglose")
        : (i.vinculacion_estado || "sin_desglose") === filterVinc))
    && (!textoF || [i.solicitante, i.provider, i.description, i.numero, i.obra_nombre, i.obra_referencia, i.created_by_name]
        .some(v => String(v || "").toLowerCase().includes(textoF))));
  const totalItems = items.reduce((s, i) => s + (Number(i.total) || 0), 0);   // Fix CQ (2026-09-14)
  // Fix EN (2026-09-29): sin IVA (exenta o sin comprobante), el monto es el total.
  const factorIva = 1 + (TASAS_IVA[form.iva_tipo] ?? 0.1);   // Fix EZ: 10 %, 5 % o sin IVA
  // Fix FA (2026-10-06): "se cera el Total Gastado" al cerrar la caja y "deben visualizarse
  // los montos con IVA incluido" (CEN): lo gastado con IVA que todavía no está en un cierre.
  // Fix GC (2026-10-14): una compra solicitada, aprobada o rechazada todavía no salió de la caja.
  const totalGastado = filtered.filter(i => !i.cierre_id && !compraSinResolver(i)).reduce((s, i) => s + (i.total_with_iva || 0), 0);
  const porAprobar = pettyItems.filter(i => i.compra_estado === "solicitada").length;   // Fix GC
  const porPagar = pettyItems.filter(i => i.compra_estado === "aprobada").length;
  const sinComprobante = pettyItems.filter(i => i.pendiente_comprobante).length;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Caja Chica</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gastos menores por proyecto · Límite 5.000.000 PYG c/u</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" data-testid="refresh-btn">
            <RefreshCw size={16} />
          </button>
          {/* Fix FA (2026-10-06): "Con este proceso el botón Ingreso pierde funcionalidad, por lo
              que puede eliminarse" (CEN). La plata entra con «Registrar reposición» del cierre. */}
          {aprob && (
            <button
              onClick={() => setAprobModal({ roles: [...(aprob.roles || [])], user_ids: [...(aprob.user_ids || [])] })}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
              data-testid="petty-aprobadores-btn"
            >
              <UserCheck size={15} /> Quién aprueba
            </button>
          )}
          {canCaja && (
            <>
              <button
                onClick={() => { setFondoInput(saldo?.fondo ? String(saldo.fondo) : ""); setCierreModal(true); }}
                className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-xl text-sm font-semibold transition-colors"
                data-testid="petty-cierre-btn"
              >
                <FileText size={15} /> Cerrar caja
              </button>
            </>
          )}
          {canCreate && (
            <button
              onClick={() => setShowNew(true)}
              className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors shadow-sm"
              data-testid="new-petty-btn"
            >
              <Plus size={15} /> Nuevo Gasto
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          // Fix DQ (2026-09-22): el saldo sale de todos los ingresos menos todos los
          // gastos con IVA; los otros tres siguen mirando lo que muestran los filtros.
          { label: "Saldo de caja chica", value: saldo ? saldo.saldo : 0, icon: Wallet,
            color: saldo && saldo.saldo < 0 ? "text-red-600" : "text-emerald-600",
            bg: saldo && saldo.saldo < 0 ? "bg-red-50" : "bg-emerald-50", testid: "petty-saldo" },
          { label: "Total gastado (sin cerrar, con IVA)", value: totalGastado, icon: TrendingDown, color: "text-red-600", bg: "bg-red-50", testid: "petty-total-gastado" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid={s.testid || undefined}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                <s.icon size={18} className={s.color} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                <p className={`text-lg font-bold ${s.color}`}>{formatPYG(s.value)}</p>
                {s.testid === "petty-saldo" && saldo && (
                  <p className="text-[11px] text-gray-400">Ingresos {formatPYG(saldo.ingresos)} − gastos {formatPYG(saldo.gastos)}</p>
                )}
                {s.testid === "petty-saldo" && saldo?.fondo > 0 && (
                  <p className="text-[11px] text-gray-400" data-testid="petty-fondo">Fondo fijo {formatPYG(saldo.fondo)}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Fix DQ (2026-09-22): los cierres de caja, que son las solicitudes de reposición. */}
      {(cierres.length > 0 || (saldo && saldo.gastos_sin_cerrar > 0)) && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid="petty-cierres">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-900 text-sm">Cierres de caja chica</h3>
            {saldo && (
              <span className="text-xs text-gray-500" data-testid="petty-sin-cerrar">
                {saldo.gastos_sin_cerrar} gasto(s) sin cerrar · {formatPYG(saldo.monto_sin_cerrar)}
              </span>
            )}
          </div>
          {cierres.length === 0 ? (
            <p className="text-xs text-gray-400">Todavía no se cerró ningún período.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {cierres.map(c => (
                <li key={c._id} className="py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs" data-testid={`petty-cierre-${c._id}`}>
                  <span className="font-mono font-bold text-gray-700">{c.numero}</span>
                  <span className="text-gray-500 flex-1">{formatDate(c.desde)} → {formatDate(c.hasta)} · {c.cantidad_gastos} gasto(s)</span>
                  <span className="font-semibold text-gray-800">{formatPYG(c.monto_reposicion)}</span>
                  <span className={`px-2 py-0.5 rounded-full font-bold ${c.estado === "repuesto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {c.estado === "repuesto" ? "Repuesto" : "Reposición solicitada"}
                  </span>
                  {canCaja && c.estado !== "repuesto" && (
                    <button onClick={() => reponerCierre(c)} disabled={guardandoCaja}
                      className="text-[11px] font-semibold text-emerald-700 hover:underline" data-testid={`petty-reponer-${c._id}`}>
                      Registrar reposición
                    </button>
                  )}
                  {/* Fix EZ (2026-10-05): "El cierre debe tener un informe en pdf". */}
                  <button onClick={() => descargarPdfCierre(c)}
                    className="text-[11px] font-semibold text-[#2E6DA4] hover:underline" data-testid={`petty-cierre-pdf-${c._id}`}>
                    PDF
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Fix DQ (2026-09-22): registrar una reposición. */}
      <Modal open={!!ingreso} onClose={() => setIngreso(null)} title="Ingreso a caja chica">
        {ingreso && (
          <form onSubmit={guardarIngreso} className="p-6 space-y-4" data-testid="petty-ingreso-modal">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Monto (PYG) *</label>
              <input type="number" min="0" value={ingreso.monto} onChange={e => setIngreso({ ...ingreso, monto: e.target.value })}
                placeholder="Ej: 5000000" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="petty-ingreso-monto" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha</label>
                <input type="date" value={ingreso.fecha} onChange={e => setIngreso({ ...ingreso, fecha: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="petty-ingreso-fecha" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Medio</label>
                <select value={ingreso.medio} onChange={e => setIngreso({ ...ingreso, medio: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white" data-testid="petty-ingreso-medio">
                  {MEDIOS_INGRESO.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Origen / concepto</label>
              <input value={ingreso.origen} onChange={e => setIngreso({ ...ingreso, origen: e.target.value })}
                placeholder="Ej: Reposición del cierre CCC-2609-0001" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="petty-ingreso-origen" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">N° comprobante</label>
                <input value={ingreso.numero_comprobante} onChange={e => setIngreso({ ...ingreso, numero_comprobante: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="petty-ingreso-comprobante" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Autorizado por</label>
                <input value={ingreso.autorizado_por} onChange={e => setIngreso({ ...ingreso, autorizado_por: e.target.value })}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="petty-ingreso-autorizado" />
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setIngreso(null)} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-semibold">Cancelar</button>
              <button type="submit" disabled={guardandoCaja} className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-xl py-2.5 text-sm font-semibold" data-testid="petty-ingreso-guardar">
                {guardandoCaja ? "Guardando…" : "Registrar ingreso"}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Fix DQ (2026-09-22): cerrar el período y dejar documentada la solicitud de reposición. */}
      <Modal open={cierreModal} onClose={() => setCierreModal(false)} title="Cerrar caja chica">
        <div className="p-6 space-y-4" data-testid="petty-cierre-modal">
          <p className="text-sm text-gray-600">
            Se cierran los gastos que todavía no pertenecen a ningún cierre y queda documentada la solicitud de reposición.
            Los gastos cerrados ya no se pueden editar.
          </p>
          {/* Fix EZ (2026-10-05): fondo fijo — se deposita la diferencia para volver al fondo. */}
          {saldo && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm space-y-0.5" data-testid="petty-cierre-resumen">
              <p><strong>{saldo.gastos_sin_cerrar}</strong> gasto(s) sin cerrar · {formatPYG(saldo.monto_sin_cerrar)}</p>
              {saldo.fondo > 0 && <p>Fondo fijo: <strong>{formatPYG(saldo.fondo)}</strong> · Saldo actual: <strong>{formatPYG(saldo.saldo)}</strong></p>}
              <p>Monto a reponer{saldo.fondo > 0 ? " (para volver al fondo)" : ""}: <strong data-testid="petty-cierre-reposicion">{formatPYG(saldo.reposicion ?? saldo.monto_sin_cerrar)}</strong></p>
            </div>
          )}
          {canFondo && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fondo fijo asignado (Gs.)</label>
              <div className="flex gap-2">
                <input type="number" min="0" value={fondoInput} onChange={e => setFondoInput(e.target.value)} placeholder="Ej: 5000000"
                  className="flex-1 min-w-0 border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="petty-fondo-input" />
                <button type="button" onClick={guardarFondo} disabled={guardandoCaja}
                  className="border border-[#2E6DA4] text-[#2E6DA4] rounded-xl px-3 py-2 text-sm font-semibold disabled:opacity-60" data-testid="petty-fondo-guardar">
                  Guardar
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Sin fondo fijo se repone lo gastado.</p>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Observaciones</label>
            <textarea value={cierreObs} onChange={e => setCierreObs(e.target.value)} rows={3}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="petty-cierre-obs" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => setCierreModal(false)} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-semibold">Cancelar</button>
            <button type="button" onClick={cerrarCaja} disabled={guardandoCaja || !(saldo && saldo.gastos_sin_cerrar > 0)}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-60 text-white rounded-xl py-2.5 text-sm font-semibold" data-testid="petty-cierre-confirmar">
              {guardandoCaja ? "Cerrando…" : "Cerrar y solicitar reposición"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Aviso */}
      <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start gap-3 text-sm text-amber-800">
        <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold">Límite: 5.000.000 PYG por proyecto</p>
          <p className="text-xs mt-0.5">No se pueden adquirir materiales de instalación con caja chica</p>
        </div>
      </div>

      {/* Fix GC (2026-10-14): compras que vienen de Análisis Ppto esperando aprobación, pago o comprobante. */}
      {(porAprobar + porPagar + sinComprobante) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-800 flex items-center gap-x-4 gap-y-1 flex-wrap" data-testid="petty-compras-pendientes">
          <span className="flex items-center gap-1.5 font-semibold"><Wallet size={14} /> Compras desde Análisis Ppto:</span>
          {porAprobar > 0 && <button onClick={() => setFilterCompra("solicitada")} className="underline font-semibold" data-testid="petty-pend-aprobar">{porAprobar} por aprobar</button>}
          {porPagar > 0 && <button onClick={() => setFilterCompra("aprobada")} className="underline font-semibold" data-testid="petty-pend-pagar">{porPagar} por pagar</button>}
          {sinComprobante > 0 && <button onClick={() => setFilterCompra("pendiente_comprobante")} className="underline font-semibold" data-testid="petty-pend-comprobante">{sinComprobante} pagada(s) sin comprobante</button>}
        </div>
      )}

      {/* Fix CT (2026-09-14): "mediante un filtro ya sea por nombre del solicitante,
          por obra o por local, el que pidió puede ubicar su pedido en caja chica"
          (Sergio, 14/09). "Local" = los gastos que no van a ninguna obra, o sea los de
          administración: están como una opción más del filtro de obra. */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={filterSolicitante}
          onChange={e => setFilterSolicitante(e.target.value)}
          placeholder="Buscar por solicitante, proveedor o descripción"
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm min-w-[280px] focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
          data-testid="filter-solicitante"
        />
        {/* Fix ED (2026-09-24): con buscador. */}
        <SelectBuscable
          value={filterObra}
          onChange={v => setFilterObra(v)}
          options={[{ value: "__sin_obra__", label: "Local — administración (sin obra)" },
            ...obras.map(o => ({ value: o._id, label: `${o.codigo} · ${o.nombre}` }))]}
          vacio="Todas las obras y el local"
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700 min-w-[220px] max-w-[320px]"
          testId="filter-obra-select"
        />
        <SelectBuscable
          value={filterProject}
          onChange={v => setFilterProject(v)}
          options={projects.map(p => ({ value: p._id, label: `${p.code} · ${p.client || "—"} · ${p.name}` }))}
          vacio="Todos los proyectos"
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700 min-w-[220px] max-w-[320px]"
          testId="filter-project-select"
        />
        <select
          value={filterVinc}
          onChange={e => setFilterVinc(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
          data-testid="filter-vinculacion"
        >
          <option value="">Toda la vinculación</option>
          <option value="pendiente">Pendiente de vinculación</option>
          <option value="cerrada">Vinculación cerrada</option>
          <option value="indirecto">Gastos indirectos</option>
        </select>
        {pettyItems.some(i => i.compra_estado) && (
          <select
            value={filterCompra}
            onChange={e => setFilterCompra(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
            data-testid="filter-compra-estado"
          >
            <option value="">Todos los gastos y solicitudes</option>
            <option value="solicitada">Solicitadas (a aprobar)</option>
            <option value="aprobada">Aprobadas (a pagar)</option>
            <option value="pagada">Pagadas</option>
            <option value="pendiente_comprobante">Pagadas sin comprobante</option>
            <option value="rechazada">Rechazadas</option>
          </select>
        )}
        {(filterSolicitante || filterObra || filterProject || filterVinc || filterCompra) && (
          <button onClick={() => { setFilterSolicitante(""); setFilterObra(""); setFilterProject(""); setFilterVinc(""); setFilterCompra(""); }}
            className="text-xs font-semibold text-gray-500 hover:text-gray-700 underline" data-testid="limpiar-filtros-petty">
            Limpiar filtros
          </button>
        )}
        <span className="text-sm text-gray-400">{filtered.length} registros</span>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <Banknote size={44} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-500 font-medium">No hay gastos de caja chica</p>
          {canCreate && (
            <button onClick={() => setShowNew(true)} className="mt-4 text-[#2E6DA4] text-sm font-semibold hover:underline">
              + Registrar primer gasto
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(item => (
            <div key={item._id} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow" data-testid="petty-card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{item.description}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-gray-400">{item.tipo_gasto === "indirecto" ? "Costo indirecto" : (item.obra_nombre || (item.obra_referencia ? `Ref.: ${item.obra_referencia}` : item.project_name))}</span>
                    <span className="text-gray-200">·</span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{item.category}</span>
                    {/* Fix FZ (2026-10-14): gasto indirecto = no se imputa a obra ni presupuesto; queda en Costos indirectos (OPE). */}
                    {item.tipo_gasto === "indirecto" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700" data-testid={`petty-indirecto-${item._id}`}
                        title="No se imputa a una obra ni a un presupuesto: queda también en Costos indirectos (OPE)">
                        Gasto indirecto
                      </span>
                    )}
                    {/* Fix GC (2026-10-14): estado de la compra que viene de Análisis Ppto. */}
                    {item.compra_estado && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${COMPRA_BADGE[item.compra_estado] || "bg-gray-100 text-gray-600"}`}
                        data-testid={`petty-compra-estado-${item._id}`}>
                        {COMPRA_LABEL[item.compra_estado] || item.compra_estado}
                      </span>
                    )}
                    <span className="text-gray-200">·</span>
                    <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
                    {/* Fix CQ (2026-09-14): el desglose de la factura, si lo hay */}
                    {(item.items || []).length > 0 && (
                      <>
                        <span className="text-gray-200">·</span>
                        <span className="text-xs text-purple-600 font-semibold" data-testid={`petty-items-count-${item._id}`}>
                          {item.items.length} ítem(s){item.lectura_validada_por ? ` · validado por ${item.lectura_validada_por}` : ""}
                        </span>
                      </>
                    )}
                    {/* Fix DQ (2026-09-22): un gasto sin desglose tampoco está imputado. */}
                    {(item.items || []).length === 0 && item.tipo_gasto !== "indirecto" && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                        data-testid={`petty-sin-desglose-${item._id}`}>
                        Pendiente imputación a Pto — sin desglose
                      </span>
                    )}
                    {/* Fix CT (2026-09-14): el estado de la imputación contra el presupuesto. */}
                    {(item.items || []).length > 0 && item.tipo_gasto !== "indirecto" && (
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${item.vinculacion_estado === "cerrada" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                        data-testid={`petty-vinc-estado-${item._id}`}>
                        {item.vinculacion_estado === "cerrada"
                          ? "Imputado al Pto"
                          : `Pendiente imputación a Pto${item.lineas_sin_vincular ? ` (${item.lineas_sin_vincular})` : ""}`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5" data-testid={`petty-solicitante-${item._id}`}>
                    Solicitante: <span className="text-gray-600 font-medium">{item.solicitante || "—"}</span>
                    {item.autorizado_por ? <> · Autorizado por: <span className="text-gray-600 font-medium">{item.autorizado_por}</span></> : null}
                    {" · "}Registrado por: {item.created_by_name}
                    {item.cierre_numero ? <> · Cierre <span className="font-mono">{item.cierre_numero}</span></> : null}
                  </p>
                  {item.compra_estado && (
                    <p className="text-[11px] text-gray-500 mt-0.5" data-testid={`petty-compra-detalle-${item._id}`}>
                      <span className="font-mono">{item.numero}</span> · AP <span className="font-mono">{item.ap_number}</span> · Proveedor: <span className="text-gray-700 font-medium">{item.provider}</span>
                      {item.approved_by_name ? <> · Aprobó: {item.approved_by_name}</> : null}
                      {item.paid_by_name ? <> · Pagó: {item.paid_by_name}</> : null}
                      {item.compra_estado === "rechazada" ? <> · Rechazó: {item.rejected_by_name} — «{item.rejected_reason}»</> : null}
                    </p>
                  )}
                  {/* Fix GC: el comprobante cargado se lee con IA y se compara con lo pagado. */}
                  {item.pendiente_comprobante && (
                    <p className="text-[11px] font-semibold text-amber-700 mt-0.5 flex items-center gap-1" data-testid={`petty-pend-comp-${item._id}`}>
                      <AlertTriangle size={11} /> Pagada: falta cargar el comprobante (factura o recibo)
                    </p>
                  )}
                  {item.comprobante_verificacion && (
                    <p className={`text-[11px] mt-0.5 flex items-start gap-1 ${item.comprobante_verificacion.estado === "verificado" ? "text-emerald-700" : "text-amber-700"}`}
                      data-testid={`petty-verif-${item._id}`}>
                      {item.comprobante_verificacion.estado === "verificado"
                        ? <><CheckCircle2 size={11} className="mt-0.5 flex-shrink-0" /> Comprobante verificado por IA</>
                        : <><AlertTriangle size={11} className="mt-0.5 flex-shrink-0" /> Comprobante con diferencias: {(item.comprobante_verificacion.diferencias || []).join(" · ")}</>}
                    </p>
                  )}
                  {(item.items || []).length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {item.items.map((l, i) => (
                        <li key={i} className="text-[11px] text-gray-500 flex justify-between gap-3 max-w-2xl" data-testid={`petty-linea-${item._id}-${i}`}>
                          <span className="truncate">{l.cantidad ? `${l.cantidad}${l.unidad ? " " + l.unidad : ""} × ` : ""}{l.descripcion}</span>
                          {/* Fix CT (2026-09-14): la leyenda por línea. */}
                          <span className={`flex-shrink-0 text-[10px] ${lineaVinculada(l) || item.tipo_gasto === "indirecto" ? "text-gray-400" : "text-amber-600 font-semibold"}`}>
                            {/* Fix FH (2026-10-08): con categoría distinta de Material (del Pto.), se ve la categoría. */}
                            {lineaVinculada(l)
                              ? [l.categoria && l.categoria !== "listado" ? categoriaLabel(l.categoria) : "",
                                 l.item_uid ? (l.item_label || "imputada") : "", l.nivel_label, l.tarea_nombre].filter(Boolean).join(" · ")
                              : (item.tipo_gasto === "indirecto" ? "" : "Pendiente imputación a Pto")}
                            {/* Fix HM (2026-10-22): una compra que viene de Compras debe llegar con nivel y tarea: lo que falta se ve. */}
                            {item.compra_estado && l.item_uid && (!l.nivel_id || !l.tarea_numero) && (
                              <span className="ml-1 text-amber-600 font-semibold" data-testid={`petty-linea-falta-${item._id}-${i}`}>
                                {[!l.nivel_id ? "falta nivel" : "", !l.tarea_numero ? "falta tarea" : ""].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </span>
                          <span className="tabular-nums flex-shrink-0">{formatPYG(l.total || 0)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-gray-900">{formatPYG(item.total_with_iva)}</p>
                  <p className="text-xs text-gray-400">Neto: {formatPYG(item.amount)}</p>
                  {item.iva_tipo === "exenta"   /* Fix EN (2026-09-29) */
                    ? <p className="text-xs text-gray-400" data-testid={`petty-sin-iva-${item._id}`}>Sin IVA</p>
                    : <p className="text-xs text-orange-500">IVA {item.iva_tipo === "5" ? "5" : "10"}%: {formatPYG(item.iva_amount)}</p>}
                  {/* Fix EZ (2026-10-05): comprobante, y edición mientras no esté en un cierre. */}
                  <p className="text-[11px] text-gray-400" data-testid={`petty-comprobante-${item._id}`}>
                    {item.compra_estado && !item.comprobante_cargado ? "Comprobante pendiente" : (sinFactura(item) ? "Sin factura" : `Fact. ${item.invoice_number || item.numero_comprobante}`)}
                  </p>
                  {/* Fix HJ (2026-10-21): "en caja chica debe quedar subida la factura también" (CEN). */}
                  {!item.compra_estado && (item.factura_cargada ? (
                    <button onClick={() => window.open(`${API}/purchases/petty-cash/${item._id}/factura?inline=1`, "_blank")}
                      className="text-[11px] font-semibold text-[#2E6DA4] hover:underline" data-testid={`petty-ver-factura-${item._id}`}>Ver factura</button>
                  ) : (!sinFactura(item) && (
                    <p className="text-[11px] font-semibold text-amber-600" data-testid={`petty-sin-archivo-${item._id}`}>Falta subir la factura</p>
                  )))}
                  {!item.compra_estado && canCreate && (!item.cierre_id || !item.factura_cargada) && (
                    <button onClick={() => { setFacturaDe(item); facturaRef.current && facturaRef.current.click(); }} disabled={trabajando === item._id}
                      className="block ml-auto text-[11px] font-semibold text-gray-600 hover:underline disabled:opacity-50" data-testid={`petty-adjuntar-factura-${item._id}`}>
                      {item.factura_cargada ? "Reemplazar factura" : "Subir factura"}
                    </button>
                  )}
                  {/* Fix GC (2026-10-14): APROB / RECH / PAGAR y comprobante de la compra desde Análisis Ppto. */}
                  {item.compra_estado && (
                    <div className="mt-2 flex items-center justify-end gap-3 flex-wrap text-[11px] font-semibold" data-testid={`petty-compra-acciones-${item._id}`}>
                      {item.compra_estado === "solicitada" && canAprobarCompra && (
                        <button onClick={() => accionCompra(item, "aprobar", null, `Solicitud ${item.numero} aprobada`)} disabled={trabajando === item._id}
                          className="text-emerald-700 hover:underline disabled:opacity-50" data-testid={`petty-aprobar-${item._id}`}>Aprobar</button>
                      )}
                      {item.compra_estado === "aprobada" && canAprobarCompra && (pagando === item._id ? (
                        <span className="text-blue-700" data-testid={`petty-pagar-confirmar-${item._id}`}>
                          ¿Pagar {formatPYG(item.total_with_iva)}? <button onClick={() => accionCompra(item, "pagar", null, `Solicitud ${item.numero} pagada`)} disabled={trabajando === item._id} className="underline" data-testid={`petty-pagar-si-${item._id}`}>Sí</button>
                          {" · "}<button onClick={() => setPagando(null)} className="text-gray-500 underline">No</button>
                        </span>
                      ) : (
                        <button onClick={() => setPagando(item._id)} className="text-blue-700 hover:underline" data-testid={`petty-pagar-${item._id}`}>Pagar</button>
                      ))}
                      {["solicitada", "aprobada"].includes(item.compra_estado) && canAprobarCompra && (
                        <button onClick={() => setRechazando({ gasto: item, motivo: "" })} className="text-red-600 hover:underline" data-testid={`petty-rechazar-${item._id}`}>Rechazar</button>
                      )}
                      {item.compra_estado === "pagada" && !item.cierre_id && canComprobante && (
                        <button onClick={() => { setComprobanteDe(item); comprobanteRef.current && comprobanteRef.current.click(); }} disabled={trabajando === item._id}
                          className="inline-flex items-center gap-1 text-[#2E6DA4] hover:underline disabled:opacity-50" data-testid={`petty-comprobante-btn-${item._id}`}>
                          {trabajando === item._id ? <Loader2 size={11} className="animate-spin" /> : <Paperclip size={11} />}
                          {item.comprobante_cargado ? "Reemplazar comprobante" : "Cargar comprobante"}
                        </button>
                      )}
                      {item.comprobante_cargado && (
                        <button onClick={() => window.open(`${API}/purchases/petty-cash/${item._id}/comprobante?inline=1`, "_blank")}
                          className="text-gray-600 hover:underline" data-testid={`petty-ver-comprobante-${item._id}`}>Ver comprobante</button>
                      )}
                    </div>
                  )}
                  {canCreate && !item.cierre_id && (
                    <div className="mt-2 flex items-center justify-end gap-3 text-[11px] font-semibold">
                      {!item.compra_estado && (
                        <button onClick={() => abrirEdicion(item)} className="inline-flex items-center gap-1 text-gray-600 hover:underline"
                          data-testid={`petty-editar-btn-${item._id}`}>
                          <Pencil size={11} /> Editar
                        </button>
                      )}
                      {item.estado === "pendiente" && (borrar === item._id ? (
                        <span className="text-red-700" data-testid={`petty-borrar-confirmar-${item._id}`}>
                          ¿Eliminar? <button onClick={() => eliminarGasto(item)} className="underline" data-testid={`petty-borrar-si-${item._id}`}>Sí</button>
                          {" · "}<button onClick={() => setBorrar(null)} className="text-gray-500 underline">No</button>
                        </span>
                      ) : (
                        <button onClick={() => setBorrar(item._id)} className="text-red-600 hover:underline" data-testid={`petty-borrar-btn-${item._id}`}>Eliminar</button>
                      ))}
                    </div>
                  )}
                  {/* Fix CT (2026-09-14): la carga retroactiva la hace el responsable de la obra. */}
                  {/* Fix EF (2026-09-24): también el que no tiene desglose. */}
                  {item.tipo_gasto !== "indirecto" && (
                    <button onClick={() => setVincular(item)}
                      className="mt-2 text-[11px] font-semibold text-[#2E6DA4] hover:underline"
                      data-testid={`petty-vincular-btn-${item._id}`}>
                      {item.vinculacion_estado === "cerrada" ? "Ver imputación" : "Vincular al presupuesto"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Fix GC (2026-10-14): el comprobante se elige acá y se lee con IA. */}
      <input ref={comprobanteRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={subirComprobante} data-testid="petty-comprobante-input" />
      {/* Fix HJ (2026-10-21): la factura de un gasto ya cargado se sube acá. */}
      <input ref={facturaRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={subirFactura} data-testid="petty-factura-input" />

      <Modal open={!!rechazando} onClose={() => setRechazando(null)} title={`Rechazar solicitud ${rechazando?.gasto?.numero || ""}`}>
        <div className="space-y-3" data-testid="petty-rechazo-modal">
          <p className="text-xs text-gray-500">Las cantidades vuelven al Análisis Ppto ({rechazando?.gasto?.ap_number}) para comprarlas de otra forma. Indicá el motivo.</p>
          <textarea value={rechazando?.motivo || ""} onChange={e => setRechazando(r => ({ ...r, motivo: e.target.value }))} rows={3}
            className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" placeholder="Motivo del rechazo" data-testid="petty-rechazo-motivo" />
          <div className="flex gap-3">
            <button onClick={() => setRechazando(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2 text-sm font-medium">Cancelar</button>
            <button onClick={() => accionCompra(rechazando.gasto, "rechazar", { motivo: rechazando.motivo.trim() }, `Solicitud ${rechazando.gasto.numero} rechazada`)}
              disabled={(rechazando?.motivo || "").trim().length < 3 || trabajando === rechazando?.gasto?._id}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-50" data-testid="petty-rechazo-confirmar">
              Rechazar
            </button>
          </div>
        </div>
      </Modal>

      {/* Fix HD (2026-10-20): quién aprueba, rechaza y paga las solicitudes de compra que llegan de Análisis Ppto. */}
      <Modal open={!!aprobModal} onClose={() => setAprobModal(null)} title="Quién aprueba en Caja Chica">
        {aprobModal && aprob && (
          <div className="space-y-4" data-testid="petty-aprobadores-modal">
            <p className="text-xs text-gray-500">Aprueban, rechazan y pagan las solicitudes de compra que llegan desde Análisis Ppto los roles marcados y las personas elegidas.</p>
            {aprob.puede_editar ? (
              <>
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">Roles</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {(aprob.roles_disponibles || []).map(r => (
                      <label key={r} className="flex items-center gap-2 text-xs text-gray-700 px-2 py-1 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input type="checkbox" checked={aprobModal.roles.includes(r)} data-testid={`aprob-rol-${r}`}
                          onChange={() => setAprobModal(m => ({ ...m, roles: m.roles.includes(r) ? m.roles.filter(x => x !== r) : [...m.roles, r] }))} />
                        {r}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">Personas</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {aprobModal.user_ids.map(uid => {
                      const u = (aprob.usuarios || []).find(x => x.id === uid);
                      return (
                        <span key={uid} className="inline-flex items-center gap-1 text-[11px] bg-[#EBF3FB] text-[#1A4B7A] border border-[#2E6DA4]/20 rounded-full pl-2 pr-1 py-0.5">
                          {u ? u.name : "Usuario inactivo"}
                          <button type="button" onClick={() => setAprobModal(m => ({ ...m, user_ids: m.user_ids.filter(x => x !== uid) }))}
                            className="px-1 hover:text-red-600" title="Quitar" data-testid={`aprob-persona-quitar-${uid}`}>✕</button>
                        </span>
                      );
                    })}
                    <div className="w-full sm:w-64">
                      <SelectBuscable value="" onChange={uid => uid && setAprobModal(m => ({ ...m, user_ids: [...m.user_ids, uid] }))}
                        options={(aprob.usuarios || []).filter(u => !aprobModal.user_ids.includes(u.id)).map(u => ({ value: u.id, label: `${u.name} · ${u.role}` }))}
                        vacio="+ Agregar persona" placeholderBuscar="Buscar persona..."
                        className="w-full border border-dashed border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white" testId="aprob-persona" />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-600" data-testid="aprob-solo-lectura">Esta lista la cambian la Administración y la Dirección General.</p>
            )}
            <div className="rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600" data-testid="aprob-aprueban">
              <span className="font-semibold">Aprueban ahora:</span> {(aprob.aprueban || []).join(", ") || "nadie"}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setAprobModal(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2 text-sm font-medium" data-testid="aprob-cerrar">
                {aprob.puede_editar ? "Cancelar" : "Cerrar"}
              </button>
              {aprob.puede_editar && (
                <button onClick={guardarAprob} disabled={guardandoAprob || (!aprobModal.roles.length && !aprobModal.user_ids.length)}
                  className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-50" data-testid="aprob-guardar">
                  {guardandoAprob ? "Guardando..." : "Guardar"}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {vincular && <VincularModal gasto={vincular} onClose={() => setVincular(null)} onSaved={() => { setVincular(null); fetchData(); }} />}

      {/* Modal Nuevo Gasto */}
      <Modal open={showNew} onClose={cerrarModal} title={editando ? `Editar gasto ${editando.numero || ""}` : "Nuevo Gasto de Caja Chica"}>
        <form onSubmit={handleSave} className="space-y-4">
          {/* AI Extract */}
          <div className="rounded-xl border border-dashed border-purple-200 bg-purple-50/50 p-3">
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleAIUpload} data-testid="petty-ai-file-input" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={aiLoading}
              className="w-full flex items-center justify-center gap-2 bg-white border border-purple-300 hover:bg-purple-100 text-purple-700 rounded-lg py-2.5 text-sm font-semibold transition-all disabled:opacity-60"
              data-testid="petty-ai-extract-btn"
            >
              {aiLoading ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {aiLoading ? "Analizando con IA..." : "Cargar factura con IA"}
            </button>
            <p className="text-[11px] text-purple-600/80 mt-1.5 text-center">PDF o imagen. La IA extrae monto, descripción, fecha, proveedor e IVA.</p>
            {/* Fix HJ (2026-10-21): la factura queda subida con el gasto. */}
            <input ref={soloFacturaRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={elegirSoloFactura} data-testid="petty-factura-file-input" />
            <div className="mt-1.5 text-center text-[11px]" data-testid="petty-factura-adjunta">
              {factura
                ? <span className="text-emerald-700 font-semibold">Factura que se guarda con el gasto: {factura.name} · <button type="button" onClick={() => setFactura(null)} className="underline" data-testid="petty-factura-quitar">Quitar</button></span>
                : (editando && editando.factura_cargada
                  ? <span className="text-emerald-700 font-semibold">Factura subida: {editando.factura_name} (si elegís otro archivo, lo reemplaza)</span>
                  : <span className="text-gray-500">La factura que elijas queda guardada con el gasto.</span>)}
              {" "}<button type="button" onClick={() => soloFacturaRef.current?.click()} className="text-purple-700 underline" data-testid="petty-factura-solo-btn">Adjuntar la factura sin leerla con IA</button>
            </div>
            {aiInfo && (
              <div className={`mt-2 rounded-lg px-3 py-2 text-xs ${aiInfo.requires_manual_review ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-emerald-100 text-emerald-800 border border-emerald-200"}`} data-testid="petty-ai-result-banner">
                {aiInfo.requires_manual_review
                  ? `⚠ La IA no está del todo segura (confianza ${aiInfo.confidence}%). Revisá los valores antes de registrar.`
                  : `IA confianza ${aiInfo.confidence}% · IVA ${formatPYG(aiInfo.iva_amount || 0)} · Total ${formatPYG(aiInfo.total_with_iva || 0)}`}
                {aiInfo.ruc && <span className="ml-1">· RUC: {aiInfo.ruc}</span>}
              </div>
            )}
          </div>

          {/* Fix FZ (2026-10-14): "Agregar una celda desplegable que indique si el gasto es «Gasto Directo
              por Caja Chica» o «Gasto Indirecto por Caja Chica»" (CEN). El directo carga el resto de los datos
              como siempre; el indirecto no lleva obra ni presupuesto y queda también en Costos indirectos (OPE). */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tipo de gasto *</label>
            <select
              value={form.tipo_gasto}
              disabled={!!editando}
              onChange={e => {
                const t = e.target.value;
                const lista = t === "indirecto" ? CATEGORIES_INDIRECTO : CATEGORIES;
                setForm({ ...form, tipo_gasto: t, obra_referencia: t === "indirecto" ? "" : form.obra_referencia,
                  category: lista.includes(form.category) ? form.category : (t === "indirecto" ? "Otros" : "Gastos menores") });
              }}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="petty-tipo-select"
            >
              <option value="directo">Gasto Directo por Caja Chica</option>
              <option value="indirecto">Gasto Indirecto por Caja Chica</option>
            </select>
            {form.tipo_gasto === "indirecto" && (
              <p className="text-[11px] text-violet-600 mt-1" data-testid="petty-tipo-indirecto-aviso">
                Costo indirecto: no se imputa a una obra ni a un presupuesto. Sale de la caja y queda también en Costos indirectos (OPE) para categorizarlo y prorratearlo.
              </p>
            )}
          </div>

          {/* Fix CQ (2026-09-14): "cada carga debe incluir el nombre del solicitante
              y la referencia de a qué obra debe ir imputada" (Sergio, 14/09). La obra
              manda: al elegirla se completa solo el proyecto al que pertenece. */}
          {form.tipo_gasto !== "indirecto" && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Obra (referencia)</label>
            {/* Fix EF (2026-09-24): "solo debe cargarse un texto como referencia de la obra
                para que el solicitante realice la vinculación posteriormente". */}
            <input
              value={form.obra_referencia}
              onChange={e => setForm({ ...form, obra_referencia: e.target.value })}
              placeholder="Ej: Torre Jejui (vacío si es del local)"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="petty-obra-referencia"
            />
            <p className="text-[11px] text-gray-400 mt-1">La obra y el ítem del presupuesto se eligen después, en "Vincular al presupuesto".</p>
          </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Solicitante *</label>
            <input
              value={form.solicitante}
              onChange={e => setForm({ ...form, solicitante: e.target.value })}
              placeholder="Quién pidió el gasto"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="petty-solicitante-input"
            />
            <p className="text-[11px] text-gray-400 mt-1">Vos quedás registrado aparte como quien carga el gasto.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Autorizado por</label>
            <input
              value={form.autorizado_por || ""}
              onChange={e => setForm({ ...form, autorizado_por: e.target.value })}
              placeholder="Quién autorizó el gasto"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="petty-autorizado-input"
            />
          </div>
          {/* Fix EI (2026-09-27): sin "Proyecto" — la carga lleva solo la obra de referencia. */}
          {/* Fix EZ (2026-10-05): "los gastos sin factura van directo al libro diario, no al
              libro de compras" (CEN). Sin factura no hay crédito fiscal: va sin IVA. */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Comprobante</label>
            <select
              value={form.comprobante_tipo}
              onChange={e => setForm({ ...form, comprobante_tipo: e.target.value, iva_tipo: e.target.value === "sin_factura" ? "exenta" : form.iva_tipo })}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="petty-comprobante-select"
            >
              <option value="factura">Factura</option>
              <option value="sin_factura">Sin factura (recibo, ticket o sin documento)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Monto (PYG{form.iva_tipo === "exenta" ? "" : ", sin IVA"}) *</label>
            {/* Fix EN (2026-09-29): "Debe tener la opción de cargar sin iva. Sigue agregado 10%
                a las factura exenta o gastos sin documento" (CEN). */}
            <div className="flex gap-2">
              <input
                type="number"
                value={form.amount}
                onChange={e => setForm({ ...form, amount: e.target.value })}
                placeholder="Ej: 150000"
                className="flex-1 min-w-0 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="petty-amount-input"
              />
              <select
                value={form.iva_tipo}
                onChange={e => setForm({ ...form, iva_tipo: e.target.value })}
                disabled={form.comprobante_tipo === "sin_factura"}
                className="w-32 border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="petty-iva-select"
              >
                <option value="10">IVA 10%</option>
                <option value="5">IVA 5%</option>
                <option value="exenta">Sin IVA</option>
              </select>
            </div>
            {form.amount > 0 && (
              <p className="text-xs text-gray-400 mt-1" data-testid="petty-iva-preview">
                {form.iva_tipo === "exenta"
                  ? <>Sin IVA → Total: {formatPYG(Number(form.amount))}</>
                  : <>IVA ({form.iva_tipo === "5" ? "5" : "10"}%): {formatPYG(Number(form.amount) * (factorIva - 1))} → Total: {formatPYG(Number(form.amount) * factorIva)}</>}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Descripción *</label>
            <input
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Ej: Compra de conectores y tuercas"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              data-testid="petty-description-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Proveedor{form.comprobante_tipo === "factura" ? " *" : ""}</label>
              <input
                value={form.provider}
                onChange={e => setForm({ ...form, provider: e.target.value })}
                placeholder="Razón social"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="petty-provider-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha</label>
              <input
                type="date"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="petty-date-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">{form.comprobante_tipo === "factura" ? "N° Factura *" : "N° comprobante"}</label>
              <input
                value={form.invoice_number}
                onChange={e => setForm({ ...form, invoice_number: e.target.value })}
                placeholder="001-001-0000001"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
                data-testid="petty-invoice-input"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">{form.tipo_gasto === "indirecto" ? "Categoría (Costos indirectos)" : "Categoría"}</label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2E6DA4]/30"
              >
                {(form.tipo_gasto === "indirecto" ? CATEGORIES_INDIRECTO : CATEGORIES).map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          {/* Fix CQ (2026-09-14): desglose de la factura por ítem. Lo trae la IA y lo
              corrige el que carga; sin tildar la validación no se guarda. */}
          <div className="rounded-xl border border-gray-200 p-3" data-testid="petty-items-box">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700">Desglose de la factura</p>
              <button type="button" onClick={() => setItems(prev => [...prev, { descripcion: "", cantidad: 1, unidad: "", precio_unit: 0, total: 0 }])}
                className="text-[11px] font-semibold text-[#2E6DA4] hover:underline" data-testid="petty-add-item-btn">+ Agregar ítem</button>
            </div>
            {items.length === 0 ? (
              <p className="text-[11px] text-gray-400">Cargá la factura con IA o agregá los ítems a mano.</p>
            ) : (
              <>
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {items.map((l, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`petty-item-row-${i}`}>
                      <input value={l.descripcion} onChange={e => setItems(prev => prev.map((x, j) => j === i ? { ...x, descripcion: e.target.value } : x))}
                        placeholder="Descripción" className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1 text-xs" data-testid={`petty-item-desc-${i}`} />
                      <input type="number" value={l.cantidad} onChange={e => setItems(prev => prev.map((x, j) => j === i ? { ...x, cantidad: e.target.value, total: (Number(e.target.value) || 0) * (Number(x.precio_unit) || 0) } : x))}
                        className="w-14 border border-gray-200 rounded-lg px-1.5 py-1 text-xs text-right" data-testid={`petty-item-cant-${i}`} />
                      <input value={l.unidad || ""} onChange={e => setItems(prev => prev.map((x, j) => j === i ? { ...x, unidad: e.target.value } : x))}
                        placeholder="un" className="w-12 border border-gray-200 rounded-lg px-1.5 py-1 text-xs" data-testid={`petty-item-unidad-${i}`} />
                      <input type="number" value={l.precio_unit} onChange={e => setItems(prev => prev.map((x, j) => j === i ? { ...x, precio_unit: e.target.value, total: (Number(x.cantidad) || 0) * (Number(e.target.value) || 0) } : x))}
                        className="w-24 border border-gray-200 rounded-lg px-1.5 py-1 text-xs text-right" data-testid={`petty-item-punit-${i}`} />
                      <input type="number" value={l.total} onChange={e => setItems(prev => prev.map((x, j) => j === i ? { ...x, total: e.target.value } : x))}
                        className="w-28 border border-gray-200 rounded-lg px-1.5 py-1 text-xs text-right font-semibold" data-testid={`petty-item-total-${i}`} />
                      <button type="button" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))}
                        className="text-gray-300 hover:text-red-600 px-1" data-testid={`petty-item-del-${i}`}>✕</button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-2 text-xs" data-testid="petty-items-total">
                  <span className="text-gray-500">{items.length} ítem(s)</span>
                  <span className="font-bold text-gray-800">Suma del desglose: {formatPYG(totalItems)}</span>
                </div>
                {form.amount > 0 && Math.abs(totalItems - Number(form.amount) * factorIva) > 1 && (
                  <p className="text-[11px] text-amber-600 font-semibold mt-1" data-testid="petty-items-desvio">
                    ⚠ El desglose no coincide con el total del gasto ({formatPYG(Number(form.amount) * factorIva)}). Revisalo.
                  </p>
                )}
                <label className="flex items-start gap-2 mt-2.5 cursor-pointer">
                  <input type="checkbox" checked={validada} onChange={e => setValidada(e.target.checked)} className="mt-0.5" data-testid="petty-validar-lectura" />
                  <span className="text-[11px] text-gray-600">Revisé el desglose y <strong>valido la lectura</strong> de la factura.</span>
                </label>
              </>
            )}
          </div>

          <div className="flex gap-3 pt-3 pb-1 sticky bottom-0 bg-white border-t border-gray-100" data-testid="petty-botones">
            <button
              type="button"
              onClick={cerrarModal}
              className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
              data-testid="save-petty-btn"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : null}
              {saving ? "Guardando..." : editando ? "Guardar cambios" : "Registrar Gasto"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
